import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Avatar, Grid, TextField, FormControl, InputLabel, Select, MenuItem, List, Dialog, DialogContent, FormControlLabel, FormGroup, FormLabel, Checkbox, Chip } from "@mui/material";
import { CloseRounded } from "@mui/icons-material";
import logo_icon from '../../../assets/images/logo_icon.png';
import { DeleteSvg, EditSvg, SwitchOffSvg, SwitchOnSvg } from "../../../assets/svg/index.js";
import MainDatatable from "../../../components/common/MainDatatable.jsx";
import * as SubAdminActions from '../../../redux/actions/subAdminAction.js';
import * as RoleActions from '../../../redux/actions/roleAction.js';
import axios from 'axios';
import { api_urls } from "../../../utils/api-urls/index.js";
import Swal from "sweetalert2";

const SubAdmin = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { subAdminData } = useSelector(state => state?.subAdminReducer);
    const { rolesData } = useSelector(state => state?.roleReducer);
    const [subAdminModalVisible, setSubAdminModalVisible] = useState(false);
    const [subAdminPermission, setSubAdminPermission] = useState([]);
    const [subAdminDetail, setSubAdminDetail] = useState({ subAdminId: '', name: '', email: '', contact: '', password: '', roleId: '' });
    const [enrichedData, setEnrichedData] = useState([]);

    const handleInputField = (e) => {
        const { name, value } = e.target;
        setSubAdminDetail({ ...subAdminDetail, [name]: value });
    };

    const handleSubAdminPermission = (e) => {
        const value = e.target.value;
        const isChecked = e.target.checked;
        if (isChecked) setSubAdminPermission([...subAdminPermission, value]);
        else setSubAdminPermission(subAdminPermission.filter(item => item !== value));
    };

    const handleEdit = (data) => {
        setSubAdminModalVisible(true);
        setSubAdminDetail({
            subAdminId: data._id || '',
            name: data.name || '',
            email: data.email || '',
            contact: data.contact || '',
            password: '',
            roleId: data.roleId?._id || data.roleId || '',
        });
        setSubAdminPermission(data?.permission || []);
    };

    const handleSubmit = async () => {
        const { subAdminId, name, email, contact, password, roleId } = subAdminDetail;
        try {
            const payload = { subAdminId, name, email, contact, permission: subAdminPermission };
            if (password) payload.password = password;
            if (roleId) payload.roleId = roleId;
            const { data } = await axios.post(api_urls + 'api/admin/updateSubAdminWithRole', payload);
            if (data?.status) {
                Swal.fire({ icon: "success", title: "Sub Admin Updated", showConfirmButton: false, timer: 2000 });
                setSubAdminModalVisible(false);
                fetchSubAdmins();
            }
        } catch (err) {
            Swal.fire({ icon: "error", title: "Update Failed", text: err?.response?.data?.message || "Error", showConfirmButton: false, timer: 2000 });
        }
    };

    const handleRoleChange = (e) => {
        const roleId = e.target.value;
        setSubAdminDetail(prev => ({ ...prev, roleId }));
        if (roleId) {
            const role = rolesData.find(r => r._id === roleId);
            if (role) {
                const perms = role.permissions.filter(p => p.view || p.add || p.edit || p.delete).map(p => p.module);
                setSubAdminPermission(perms);
            }
        }
    };

    const fetchSubAdmins = async () => {
        try {
            const { data } = await axios.get(api_urls + 'api/admin/getAllSubadminsWithRoles');
            if (data?.status) {
                setEnrichedData(data.results);
                dispatch(SubAdminActions.setSubAdmin(data.results));
            }
        } catch (err) {
            dispatch(SubAdminActions.getSubAdmin());
        }
    };

    const subAdminPermissionData = [
        { name: "Dashboard" }, { name: "User" }, { name: "Sub Admin" }, { name: "Banner" },
        { name: "Category" }, { name: "Sub Category" }, { name: "Yatra" }, { name: "Sanchar" },
        { name: "Ecommerce" }, { name: "Religious" }, { name: "Static Pages" }, { name: "Astrologer" },
        { name: "Notifications" }, { name: "App Config" }, { name: "Utilities" }, { name: "Astro Mall" },
        { name: "Astro Puja" }, { name: "Services History" }, { name: "TBO Bookings" },
        { name: "Milan Sathi" }, { name: "Astrologer Admin" }, { name: "Post Banao" },
        { name: "Rojgar" }, { name: "Marketplace" }, { name: "Sanatani Manch" },
        { name: "Transaction" }, { name: "Operator Images" }, { name: "Flight Images" },
        { name: "Reports" }, { name: "App Errors" }, { name: "Roles & Logs" },
    ];

    const subAdminColumns = [
        { name: 'S.No.', selector: (row, index) => index + 1, width: '70px' },
        { name: 'Name', selector: row => row?.name },
        { name: 'Email', selector: row => row?.email },
        { name: 'Phone', selector: row => row?.contact },
        { name: 'Role', selector: row => {
            const roleName = row?.roleId?.name || 'Custom';
            return <Chip label={roleName} size="small" variant="outlined" color={row?.roleId?.name ? "primary" : "default"} />;
        }, width: '140px' },
        { name: 'Permissions', selector: row => row?.permission?.length || 0, width: '100px' },
        { name: 'Status', cell: row => (
            <div onClick={() => dispatch(SubAdminActions.changeSubAdminStatus({ subAdminId: row?._id }))} style={{ cursor: "pointer" }}>
                {row?.status === 'Active' ? <SwitchOnSvg /> : <SwitchOffSvg />}
            </div>
        ), width: '100px' },
        {
            name: 'Action',
            cell: row => (
                <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
                    <div style={{ cursor: "pointer" }} onClick={() => handleEdit(row)}><EditSvg /></div>
                    <div style={{ cursor: "pointer" }} onClick={() => dispatch(SubAdminActions.deleteSubAdmin({ subAdminId: row?._id }))}><DeleteSvg /></div>
                </div>
            ),
            width: '120px',
        },
    ];

    useEffect(() => {
        fetchSubAdmins();
        dispatch(RoleActions.getRoles());
    }, []);

    const displayData = enrichedData.length > 0 ? enrichedData : subAdminData;

    return (
        <>
            <MainDatatable data={displayData} columns={subAdminColumns} title={'List of Sub-Admin'} url={'/add-sub-admin'} />

            <Dialog open={subAdminModalVisible} maxWidth="md" fullWidth>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item lg={12} sm={12} md={12} xs={12} sx={{ marginBottom: "10px" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <div style={{ fontSize: "23px", fontWeight: "600" }}>Edit Sub-Admin</div>
                                <div onClick={() => setSubAdminModalVisible(false)} style={{ cursor: "pointer" }}><CloseRounded /></div>
                            </div>
                        </Grid>

                        <Grid item lg={6} md={6} sm={12} xs={12}>
                            <TextField label="Name" variant='outlined' fullWidth name='name' value={subAdminDetail?.name} onChange={handleInputField} />
                        </Grid>
                        <Grid item lg={6} md={6} sm={12} xs={12}>
                            <TextField label="Email" variant='outlined' fullWidth name='email' value={subAdminDetail?.email} onChange={handleInputField} />
                        </Grid>
                        <Grid item lg={6} md={6} sm={12} xs={12}>
                            <TextField label="Contact" variant='outlined' fullWidth name='contact' value={subAdminDetail?.contact} onChange={handleInputField} />
                        </Grid>
                        <Grid item lg={6} sm={12} md={12} xs={12}>
                            <TextField label="New Password (leave blank to keep)" variant='outlined' fullWidth name='password' value={subAdminDetail?.password} onChange={handleInputField} />
                        </Grid>

                        <Grid item lg={12} sm={12} md={12} xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Assign Role</InputLabel>
                                <Select value={subAdminDetail.roleId} label="Assign Role" onChange={handleRoleChange}>
                                    <MenuItem value="">No Role (Custom Permissions)</MenuItem>
                                    {rolesData.map(role => (
                                        <MenuItem key={role._id} value={role._id}>{role.name} - {role.description || 'No description'}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item lg={12} sm={12} md={12} xs={12}>
                            <FormLabel component="legend">Permissions {subAdminDetail.roleId ? '(from role)' : '(custom)'}</FormLabel>
                            <FormGroup aria-label="position" row>
                                {subAdminPermissionData.map((value, index) => {
                                    const isChecked = subAdminPermission.includes(value.name);
                                    return (
                                        <div key={index}>
                                            <FormControlLabel label={value.name}
                                                control={<Checkbox checked={isChecked} disabled={!!subAdminDetail.roleId} />}
                                                name={value.name} value={value.name}
                                                onChange={(e) => handleSubAdminPermission(e)} />
                                        </div>
                                    );
                                })}
                            </FormGroup>
                        </Grid>

                        <Grid item lg={12} sm={12} md={12} xs={12}>
                            <Grid container spacing={2}>
                                <Grid item lg={6} sm={6} md={6} xs={6}>
                                    <div onClick={handleSubmit} style={{ fontWeight: "500", backgroundColor: "#164E62", color: "#fff", padding: "10px 20px", borderRadius: "5px", cursor: "pointer", fontSize: "15px", textAlign: "center" }}>Update</div>
                                </Grid>
                                <Grid item lg={6} sm={6} md={6} xs={6}>
                                    <div onClick={() => setSubAdminModalVisible(false)} style={{ fontWeight: "500", backgroundColor: "#EDF2F5", color: "#000", padding: "10px 20px", borderRadius: "5px", cursor: "pointer", fontSize: "15px", textAlign: "center" }}>Close</div>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default SubAdmin;
